import styled from '@emotion/styled';
import {
  gradient,
  palette,
  spacing,
  radius,
  shadow,
  typography,
  zIndex,
  layout,
  bp,
  duration,
  easing,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { SCALE } from '../../motion/tokens';

// The persistent Lobbero IQ entry point (SPEC §7.1 "always one tap away").
// Aurora fill + shadow.brand at radius.circle = the reserved AI mark.
//
// BOTTOM-RIGHT, on every size. The right edge is the only one with no fixed
// furniture on it: the rail owns the left on lg/md and the whole bottom under
// md, and the Profile avatar sits in the rail's bottom slot. Anchoring right
// means the FAB never has to know how wide the rail currently is, and it puts
// the launcher on the same edge as the panel it opens (iq-chat.styled
// FloatPanel is `right`-anchored too), so the panel grows out of the button
// instead of flying across the screen.
//
// RESPONSIVE, in three steps:
//   default (>= md)  right/bottom on the normal gutter, beside the content
//   < md             the rail is now a fixed bottom tab bar (layout.navHeight
//                    + the phone's home-indicator inset), so the FAB rises
//                    clear of it rather than sitting on top of the nav
//   < sm             tighter gutter and a slightly smaller mark, because at
//                    360px a 56px circle on a 16px gutter eats a real share of
//                    the line it floats over. Never below layout.touchTarget —
//                    44px is the floor for a thumb.
//
// z-index is `sticky`, the same layer as the rail it sits beside — deliberately
// NOT `modal`. The FAB is furniture, not a dialog: at modal level it would float
// over an open form or over the notification sheet. Its own panel
// (iq-chat.styled FloatPanel) is the thing that takes `modal`.
export const Fab = styled.button`
  position: fixed;
  right: ${spacing.md};
  bottom: ${spacing.lg};
  z-index: ${zIndex.sticky};
  width: 56px;
  height: 56px;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  /* Chrome's default button padding is 1px 6px, and a button that sets none
     inherits it — which is where this control's off-grid padding came from on
     all 39 screens it appears on. The contents are flex-centred in a fixed
     56px circle, so the correct value is none. */
  padding: 0;
  border: none;
  border-radius: ${radius.circle};
  background: ${gradient.aurora};
  color: ${palette.white};
  box-shadow: ${shadow.brand};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${typography.size.xl}px;
  line-height: 1;
  /* The product's press/lift grammar: lift 1px at the base tempo, press to
     SCALE.pressed at the instant tempo. Tokens, not literals — the e2e scanner
     flags any duration that is not on the scale. */
  transition: box-shadow ${duration.base} ${easing.standard},
    transform ${duration.base} ${easing.spring};

  &:hover {
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }
  &:active {
    transform: scale(${SCALE.pressed});
    transition-duration: ${duration.instant};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${down('md')} {
    /* The rail is a bottom tab bar here, not a left column. Clear its height
       AND the home-indicator inset — on a notched phone the bar itself is
       already padded by that inset, so a FAB that only cleared navHeight would
       land inside it. layout.navHeight rather than a literal 72px, so the two
       cannot drift apart. */
    bottom: calc(
      ${layout.navHeight} + ${spacing.md} + var(--safe-bottom) +
        var(--gate-dock-lift, 0px)
    );
    /* --gate-dock-lift is set only while the clock-in gate's pinned dock is
       on screen (shifts/gate-dock.tsx), and it lifts the launcher clear of
       Clock in. Everywhere else it is unset and resolves to 0. */
  }

  ${down('sm')} {
    right: ${spacing.sm};
    width: ${layout.touchTarget};
    height: ${layout.touchTarget};
    font-size: ${typography.size.lg}px;
  }

  /* Short landscape windows — a phone turned sideways leaves ~320px of height,
     and the FAB, the tab bar and the content are all competing for it. Sit
     tighter to the bar rather than floating in the middle of the fold.
     Width-bounded as well as height-bounded: a short but WIDE window is a
     laptop with a squashed browser, where the rail is still a left column and
     there is no bottom bar to clear. */
  @media (max-width: ${bp.md - 0.02}px) and (max-height: 480px) {
    bottom: calc(
      ${layout.navHeight} + ${spacing.xs} + var(--safe-bottom) +
        var(--gate-dock-lift, 0px)
    );
  }

  /* Reduced motion is handled ONCE, by the global rule in index.css (durations
     collapse) and MotionRoot; no per-component block here. */
`;
